import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Workflow, Plus, Sparkles, Play, Pencil, Trash2, X, Loader2, History,
  Clock, Zap, CheckCircle2, AlertTriangle, Bell, Mail, Send,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";

const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function ProcessEditor({ api, agents, process, initial, onClose, onSaved }) {
  const src = process || initial || {};
  const isNew = !process?.process_id;
  const [form, setForm] = useState({
    name: src.name || "",
    agent_key: src.agent_key || "orchestrateur",
    instructions: src.instructions || "",
    schedule_type: src.schedule_type || "daily",
    schedule_time: src.schedule_time || "08:00",
    schedule_weekday: src.schedule_weekday ?? 0,
    allow_actions: src.allow_actions ?? false,
    notify: src.notify ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.instructions.trim()) { toast.error("Nom et instructions requis"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/ai/processes", form);
      else await api.put(`/ai/processes/${process.process_id}`, form);
      toast.success(isNew ? "Process créé" : "Process mis à jour");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="process-editor-dialog">
      <div className="bg-[#0f0a18] border border-white/10 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white flex items-center gap-2"><Workflow className="w-4 h-4 text-[#f7c935]" /> {isNew ? "Nouveau process" : "Modifier le process"}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="process-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Nom du process *</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex : Brief commercial du matin"
              className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#f7c935]/40 outline-none" data-testid="process-name-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Agent responsable</label>
            <select value={form.agent_key} onChange={(e) => set("agent_key", e.target.value)}
              className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="process-agent-select">
              {agents.map((a) => <option key={a.type} value={a.type}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Instructions pour l'agent *</label>
            <textarea value={form.instructions} onChange={(e) => set("instructions", e.target.value)} rows={5}
              placeholder="Décrivez ce que l'agent doit faire à chaque exécution…"
              className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-300 focus:border-[#f7c935]/40 outline-none" data-testid="process-instructions-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Déclenchement</label>
              <select value={form.schedule_type} onChange={(e) => set("schedule_type", e.target.value)}
                className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="process-schedule-type">
                <option value="daily">Quotidien</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="manual">À la demande</option>
              </select>
            </div>
            {form.schedule_type === "weekly" && (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Jour</label>
                <select value={form.schedule_weekday} onChange={(e) => set("schedule_weekday", Number(e.target.value))}
                  className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="process-weekday">
                  {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            {form.schedule_type !== "manual" && (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Heure (Paris)</label>
                <input type="time" value={form.schedule_time} onChange={(e) => set("schedule_time", e.target.value)}
                  className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="process-time" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <div>
              <p className="text-sm text-white flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-400" /> Actions réelles autorisées</p>
              <p className="text-[10px] text-slate-500 mt-0.5">L'agent peut envoyer des emails (relances…) et créer des notifications</p>
            </div>
            <Switch checked={form.allow_actions} onCheckedChange={(v) => set("allow_actions", v)} data-testid="process-allow-actions" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
            <p className="text-sm text-slate-300 flex items-center gap-1.5"><Bell className="w-3.5 h-3.5 text-slate-400" /> Me notifier à chaque exécution</p>
            <Switch checked={form.notify} onCheckedChange={(v) => set("notify", v)} data-testid="process-notify" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-300 h-9 text-xs">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="save-process-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isNew ? "Créer le process" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DigestDialog({ api, onClose }) {
  const [settings, setSettings] = useState(null);
  const [recipientsStr, setRecipientsStr] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/ai/digest-settings").then((r) => {
      setSettings(r.data);
      setRecipientsStr((r.data.recipients || []).join(", "));
    }).catch(() => toast.error("Impossible de charger le digest"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/ai/digest-settings", {
        enabled: settings.enabled,
        time: settings.time,
        recipients: recipientsStr.split(",").map((x) => x.trim()).filter(Boolean),
      });
      setSettings(r.data);
      setRecipientsStr((r.data.recipients || []).join(", "));
      toast.success(r.data.enabled ? `Digest activé — chaque jour à ${r.data.time}` : "Digest désactivé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.put("/ai/digest-settings", {
        enabled: settings.enabled,
        time: settings.time,
        recipients: recipientsStr.split(",").map((x) => x.trim()).filter(Boolean),
      });
      const r = await api.post("/ai/digest/send-now");
      toast.success(`Digest envoyé (${r.data.runs} compte(s)-rendu(s), ${r.data.recipients} destinataire(s))`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    setSending(false);
  };

  if (!settings) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="digest-dialog">
      <div className="bg-[#0f0a18] border border-white/10 rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-[#f7c935]" /> Digest quotidien par email</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="digest-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-4">Chaque matin, recevez par email le résumé des comptes-rendus produits par vos agents depuis le dernier digest.</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
            <span className="text-sm text-slate-300">Digest activé</span>
            <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="digest-enabled" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Heure d'envoi (Paris)</label>
            <input type="time" value={settings.time} onChange={(e) => setSettings({ ...settings, time: e.target.value })}
              className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="digest-time" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Destinataires (séparés par des virgules)</label>
            <input value={recipientsStr} onChange={(e) => setRecipientsStr(e.target.value)} placeholder="vous@societe.fr, associe@societe.fr"
              className="w-full bg-[#161020] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#f7c935]/40 outline-none" data-testid="digest-recipients" />
          </div>
          {settings.last_status && (
            <p className="text-[10px] text-slate-500" data-testid="digest-last-status">{settings.last_status}</p>
          )}
        </div>
        <div className="flex justify-between gap-2 mt-5">
          <Button variant="outline" onClick={sendNow} disabled={sending || !recipientsStr.trim()}
            className="border-white/10 text-slate-300 h-9 text-xs" data-testid="digest-send-now">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Envoyer un test
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="digest-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RunViewer({ run, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="run-viewer-dialog">
      <div className="bg-[#0f0a18] border border-white/10 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div>
            <p className="font-bold text-white text-sm">{run.process_name}</p>
            <p className="text-[10px] text-slate-500">
              {run.status === "done" ? "Terminé" : run.status === "error" ? "Erreur" : "En cours"} · {new Date(run.started_at).toLocaleString("fr-FR")} · {run.trigger === "schedule" ? "planifié" : "manuel"}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="run-viewer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {run.status === "error" ? (
            <p className="text-sm text-red-400">{run.error}</p>
          ) : (
            <div className="prose prose-invert prose-sm max-w-none prose-headings:font-bold prose-h2:text-base prose-h3:text-sm prose-p:text-sm prose-li:text-sm prose-strong:text-white" data-testid="run-report">
              <ReactMarkdown>{run.report || "—"}</ReactMarkdown>
            </div>
          )}
          {(run.actions || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs font-bold text-slate-500 mb-2">ACTIONS DÉCLENCHÉES</p>
              {run.actions.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1" data-testid={`run-action-${i}`}>
                  {a.status === "done" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                  <span className="text-slate-300">{a.type} — {a.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const ProcessPanel = ({ api, agents, societeId }) => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [runningIds, setRunningIds] = useState({});
  const [runViewer, setRunViewer] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [historyRuns, setHistoryRuns] = useState([]);
  const [showDigest, setShowDigest] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = async () => {
    try {
      const r = await api.get("/ai/processes");
      setProcesses(r.data?.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(); }, [societeId]);

  const pollRun = async (runId, processId) => {
    for (let i = 0; i < 45; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      if (!mounted.current) return;
      try {
        const r = await api.get(`/ai/process-runs/${runId}`);
        if (r.data.status !== "running") {
          setRunningIds((m) => ({ ...m, [processId]: false }));
          setRunViewer(r.data);
          load();
          return;
        }
      } catch { /* keep polling */ }
    }
    setRunningIds((m) => ({ ...m, [processId]: false }));
    toast.info("L'exécution continue en arrière-plan — consultez l'historique");
  };

  const runNow = async (p) => {
    setRunningIds((m) => ({ ...m, [p.process_id]: true }));
    try {
      const r = await api.post(`/ai/processes/${p.process_id}/run`);
      toast.success("Exécution lancée…");
      pollRun(r.data.run_id, p.process_id);
    } catch (e) {
      setRunningIds((m) => ({ ...m, [p.process_id]: false }));
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleEnabled = async (p) => {
    try {
      await api.put(`/ai/processes/${p.process_id}`, { enabled: !p.enabled });
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le process « ${p.name} » et son historique ?`)) return;
    try { await api.delete(`/ai/processes/${p.process_id}`); toast.success("Process supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post("/ai/processes/suggest");
      setSuggestions(r.data?.suggestions || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    setSuggesting(false);
  };

  const addSuggestion = async (s) => {
    try {
      await api.post("/ai/processes", { ...s, notify: true, allow_actions: false });
      toast.success(`Process « ${s.name} » ajouté`);
      setSuggestions((list) => list.filter((x) => x !== s));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openHistory = async (p) => {
    setHistoryFor(p);
    try {
      const r = await api.get(`/ai/process-runs?process_id=${p.process_id}`);
      setHistoryRuns(r.data?.items || []);
    } catch { setHistoryRuns([]); }
  };

  const agentOf = (key) => agents.find((a) => a.type === key);

  return (
    <div data-testid="process-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Vos agents exécutent ces process en autonomie et vous remettent un compte-rendu.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowDigest(true)}
            className="border-white/10 text-slate-300 hover:bg-white/5 h-9 text-xs" data-testid="digest-btn">
            <Mail className="w-4 h-4 mr-1" /> Digest email
          </Button>
          <Button variant="outline" onClick={suggest} disabled={suggesting}
            className="border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10 h-9 text-xs" data-testid="suggest-processes-btn">
            {suggesting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />} Suggérer des process (IA)
          </Button>
          <Button onClick={() => setEditor({})} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="new-process-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouveau process
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>
      ) : processes.length === 0 ? (
        <div className="text-center py-14 rounded-xl border border-dashed" style={{ borderColor: "var(--border-primary)" }}>
          <Workflow className="w-12 h-12 mx-auto mb-3 text-[#f7c935]/40" />
          <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Aucun process pour l'instant</p>
          <p className="text-xs mt-1 max-w-md mx-auto" style={{ color: "var(--text-muted)" }}>
            Créez un process ou laissez l'IA vous en suggérer selon votre activité.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {processes.map((p) => {
            const ag = agentOf(p.agent_key);
            const running = runningIds[p.process_id];
            return (
              <div key={p.process_id} className={`rounded-xl border p-4 ${p.enabled ? "" : "opacity-60"}`}
                style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={`process-card-${p.process_id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate" style={{ color: "var(--text-primary)" }}>{p.name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${ag?.color || "#f7c935"}18`, color: ag?.color || "#f7c935" }}>
                        {ag?.name?.replace("Agent ", "") || p.agent_key}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {p.schedule_label}
                      </span>
                      {p.allow_actions && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5" /> Actions réelles
                        </span>
                      )}
                    </div>
                  </div>
                  <Switch checked={p.enabled} onCheckedChange={() => toggleEnabled(p)} data-testid={`process-toggle-${p.process_id}`} />
                </div>
                <p className="text-[11px] mt-2 line-clamp-2" style={{ color: "var(--text-muted)" }}>{p.instructions}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                  <span className="text-[10px]" style={{ color: "var(--text-dimmed)" }}>
                    {p.last_run
                      ? `Dernier run : ${new Date(p.last_run.started_at).toLocaleString("fr-FR")} (${p.last_run.status === "done" ? "OK" : p.last_run.status})`
                      : "Jamais exécuté"}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => runNow(p)} disabled={running} className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-400" title="Exécuter maintenant" data-testid={`run-process-${p.process_id}`}>
                      {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => openHistory(p)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" title="Historique" data-testid={`history-process-${p.process_id}`}>
                      <History className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditor(p)} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" title="Modifier" data-testid={`edit-process-${p.process_id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(p)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400" title="Supprimer" data-testid={`delete-process-${p.process_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showDigest && <DigestDialog api={api} onClose={() => setShowDigest(false)} />}
      {editor !== null && (
        <ProcessEditor api={api} agents={agents} process={editor.process_id ? editor : null}
          initial={!editor.process_id ? editor : null} onClose={() => setEditor(null)} onSaved={load} />
      )}
      {runViewer && <RunViewer run={runViewer} onClose={() => setRunViewer(null)} />}

      {suggestions !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="suggestions-dialog">
          <div className="bg-[#0f0a18] border border-white/10 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <h3 className="font-bold text-white flex items-center gap-2 text-sm"><Sparkles className="w-4 h-4 text-[#f7c935]" /> Process suggérés par l'IA</h3>
              <button onClick={() => setSuggestions(null)} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="suggestions-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {suggestions.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucune suggestion — tous les process utiles semblent déjà en place.</p>}
              {suggestions.map((s, i) => {
                const ag = agentOf(s.agent_key);
                return (
                  <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`suggestion-card-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-white">{s.name}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{s.description}</p>
                        <div className="flex gap-2 mt-1.5">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${ag?.color || "#f7c935"}18`, color: ag?.color || "#f7c935" }}>
                            {ag?.name?.replace("Agent ", "") || s.agent_key}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">
                            {s.schedule_type === "daily" ? `Quotidien ${s.schedule_time}` : s.schedule_type === "weekly" ? `Hebdo ${WEEKDAYS[s.schedule_weekday] || ""} ${s.schedule_time}` : "À la demande"}
                          </span>
                        </div>
                      </div>
                      <Button onClick={() => addSuggestion(s)} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-8 text-xs flex-shrink-0" data-testid={`add-suggestion-${i}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="history-dialog">
          <div className="bg-[#0f0a18] border border-white/10 rounded-xl w-full max-w-xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <h3 className="font-bold text-white text-sm">Historique — {historyFor.name}</h3>
              <button onClick={() => setHistoryFor(null)} className="p-1 rounded hover:bg-white/10 text-slate-400" data-testid="history-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-white/5">
              {historyRuns.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Aucune exécution</p>}
              {historyRuns.map((r) => (
                <button key={r.run_id} onClick={() => { setRunViewer(r); }} className="w-full text-left px-5 py-3 hover:bg-white/[0.03] flex items-center gap-3" data-testid={`history-run-${r.run_id}`}>
                  {r.status === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : r.status === "error" ? <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" /> : <Loader2 className="w-4 h-4 animate-spin text-slate-400 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-xs text-white">{new Date(r.started_at).toLocaleString("fr-FR")} · {r.trigger === "schedule" ? "planifié" : "manuel"}</p>
                    <p className="text-[10px] text-slate-500 truncate">{(r.report || r.error || "").slice(0, 100)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
